"use client";

import React from "react";
import { Users, CloudSun, PawPrint, MapPin, ShieldCheck, Leaf } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const benefits = [{
  title: "Right Seat Access",
  description: "Maintain your connection to the cockpit by pairing with qualified pilots who value your experience.",
  icon: Users
}, {
  title: "Technical Precision",
  description: "Structured lease-back logistics designed by pilots for pilots, ensuring regulatory compliance.",
  icon: ShieldCheck
}, {
  title: "Asset Preservation",
  description: "Extract equity for lifetime care while keeping the aircraft operational and hangared.",
  icon: MapPin
}, {
  title: "Operational Support",
  description: "A dedicated Care Advisor manages the logistics of both your home and your flight schedule.",
  icon: CloudSun
}, {
  title: "Legacy Continuity",
  description: "Pass on your knowledge to the next generation of aviators through our pairing program.",
  icon: Leaf
}, {
  title: "Safety First",
  description: "Rigorous screening for all paired pilots to ensure your aircraft is handled with ATP-level care.",
  icon: ShieldCheck
}];
export default function RightSeatProgram() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="248631" id="right-seat-program" ref={sectionRef} className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {}
          <div className={`flex flex-col gap-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary mb-4 tracking-tight uppercase">
                The Right Seat Program
              </h2>
              <p className="text-lg text-muted-foreground font-default max-w-xl leading-relaxed">
                Losing a medical certificate shouldn't mean losing your identity. Our structured lease-back programs allow you to remain PIC-adjacent, flying your own aircraft while we manage the logistics of your lifetime care.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8 mt-4">
              {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return <div key={i} data-index={i} className="flex gap-4 items-start">
                    <div className="shrink-0 mt-1 bg-secondary p-3 rounded-md border border-border shadow-sm">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-default font-semibold text-foreground mb-2 uppercase tracking-wide">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground text-sm font-default leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className={`transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <AspectRatio ratio={4 / 5} className="w-full">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/beechcraft_baron_cockpit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/beechcraft_baron_cockpit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/beechcraft_baron_cockpit.png-wvc-sizes" alt="Beechcraft Baron cockpit panel showing technical precision" className="rounded-md object-cover w-full h-full shadow-2xl border border-secondary" loading="lazy" decoding="async" />
              {}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary/10 rounded-md blur-2xl -z-10"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-secondary/30 rounded-md blur-3xl -z-10"></div>
            </AspectRatio>
          </div>

        </div>
      </div>
    </section>;
}